/**
 * 원격지원 화면 메시지 해석 — 직원 PC의 Rust(src-tauri/src/remote_capture.rs)가 Tauri Channel로 보내는 바이너리.
 *
 * ⚠ import를 넣지 말 것 — scripts/test-remote-capture-protocol.js가 typescript.transpileModule로 통째로 평가한다.
 * ⚠ 형식은 remote_capture.rs 머리 주석과 같아야 한다(little-endian).
 *   헤더 24B : [u8 version=1][u8 flags: bit0 FULL][u16 조각 수][u32 프레임 폭][u32 프레임 높이][u32 예약][u64 캡처 시각 epoch µs]
 *   조각 20B+: [u32 x][u32 y][u32 w][u32 h][u32 JPEG 길이][JPEG]
 *
 * FULL은 프레임 전체 한 장이다(시작·화면 구성 변경·따라잡기 요청). FULL이 아니면 바뀐 사각형 조각들이고,
 * 받는 쪽은 FULL을 한 번이라도 그린 캔버스에만 조각을 덧그린다.
 */

export const CAPTURE_MSG_VERSION = 1;
export const CAPTURE_HEADER_LEN = 24;
export const CAPTURE_PATCH_HEADER_LEN = 20;

export interface CapturePatch {
  x: number;
  y: number;
  w: number;
  h: number;
  /** 원본 버퍼를 가리키는 뷰(복사하지 않는다) */
  jpeg: Uint8Array;
}

export interface CaptureMessage {
  full: boolean;
  width: number;
  height: number;
  /** Rust가 화면을 뜬 시각(epoch ms) — 지연 진단용 */
  capturedAtMs: number;
  patches: CapturePatch[];
}

/** 형식이 어긋나면 null(버전 불일치·잘린 메시지·프레임 밖 조각) — 받는 쪽은 버리고 전체 프레임을 다시 청한다 */
export function parseCaptureMessage(buf: ArrayBuffer): CaptureMessage | null {
  if (buf.byteLength < CAPTURE_HEADER_LEN) return null;
  const dv = new DataView(buf);
  if (dv.getUint8(0) !== CAPTURE_MSG_VERSION) return null;
  const full = (dv.getUint8(1) & 1) !== 0;
  const count = dv.getUint16(2, true);
  const width = dv.getUint32(4, true);
  const height = dv.getUint32(8, true);
  // u64를 BigInt 없이 — epoch µs는 2^53 안이다
  const capturedAtMs = (dv.getUint32(16, true) + dv.getUint32(20, true) * 4294967296) / 1000;
  if (width === 0 || height === 0) return null;
  const patches: CapturePatch[] = [];
  let off = CAPTURE_HEADER_LEN;
  for (let i = 0; i < count; i++) {
    if (off + CAPTURE_PATCH_HEADER_LEN > buf.byteLength) return null;
    const x = dv.getUint32(off, true);
    const y = dv.getUint32(off + 4, true);
    const w = dv.getUint32(off + 8, true);
    const h = dv.getUint32(off + 12, true);
    const len = dv.getUint32(off + 16, true);
    off += CAPTURE_PATCH_HEADER_LEN;
    if (len === 0 || off + len > buf.byteLength) return null;
    if (w === 0 || h === 0 || x + w > width || y + h > height) return null;
    patches.push({ x, y, w, h, jpeg: new Uint8Array(buf, off, len) });
    off += len;
  }
  if (full && (patches.length !== 1 || patches[0].x !== 0 || patches[0].y !== 0)) return null;
  return { full, width, height, capturedAtMs, patches };
}

/** 원격지원 중 표시·기록용 — '전체 2대 3840x1088' (rs:started의 monitorLabel, 64자 상한) */
export function captureLabel(monitorCount: number, width: number, height: number): string {
  return `전체 ${Math.max(1, monitorCount)}대 ${width}x${height}`.slice(0, 64);
}

/**
 * 송출 비트레이트 — 예전 모니터 한 대(1920x1080) 2.5Mbps를 화소 수에 비례해 늘리되 6Mbps에서 멈춘다.
 * 사내망 기준이다(TURN이 생기면 다시 본다).
 */
export function captureBitrate(width: number, height: number): number {
  const base = 2_500_000;
  const k = (Math.max(1, width) * Math.max(1, height)) / (1920 * 1080);
  return Math.round(Math.min(6_000_000, Math.max(base, base * k)));
}
